import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, Check, Plus, Send, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { api, ApiError } from '@/api/client';
import { useActionsTdr, useAgents, useBudgetLignes, useCalculMission, useStructures, useTdr, type TdrSaisie } from '@/api/hooks';
import type { Agent, ResultatCalcul, Tdr } from '@/api/types';
import { voitToutLeFonds } from '@/auth/modules';
import { useSession } from '@/auth/session';
import { BoutonPdf, Chargement, ControlesListe, PageHeader } from '@/components/common';
import { ActiviteSousActivite, CircuitPrevu, Completude, Lateral, SituationLigneChoisie, useDeclareMemorial, type PointCompletude } from '@/components/documents';
import { PiecesJointes } from '@/components/pieces-jointes';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SelectNatif } from '@/components/ui/select-natif';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { cn, dateFr, montant } from '@/lib/utils';

const PROVINCES = ['Kinshasa', 'Kongo Central', 'Kwango', 'Kwilu', 'Maï-Ndombe', 'Kasaï', 'Kasaï-Central', 'Kasaï-Oriental', 'Lomami', 'Sankuru', 'Maniema', 'Sud-Kivu', 'Nord-Kivu', 'Ituri', 'Haut-Uélé', 'Bas-Uélé', 'Tshopo', 'Mongala', 'Nord-Ubangi', 'Sud-Ubangi', 'Équateur', 'Tshuapa', 'Haut-Katanga', 'Lualaba', 'Haut-Lomami', 'Tanganyika'];
const PAYS = ['Éthiopie', 'Suisse', 'Belgique', 'Rwanda', 'Ouganda', 'Kenya', 'Afrique du Sud', 'France'];
const NATURES: Record<string, string> = { PERDIEM: 'Perdiem', LOGEMENT: 'Logement', HEBERGEMENT: 'Hébergement', TRANSPORT: 'Transport', PROTOCOLE: 'Protocole', REPRESENTATION: 'Représentation', FRAIS_TERMINAUX: 'Frais terminaux', GO_PASS: 'Go-Pass' };
/** Seuils de la maquette (§ 5, étape 2) : titre 15, contexte 200, objectif global 30 caractères. */
const MIN_TITRE = 15;
const MIN_CONTEXTE = 200;
const MIN_OBJECTIF = 30;
/** Départ au moins une semaine après la soumission, sauf urgence (§ 5, étape 3). */
const DELAI_DEPART_JOURS = 7;

interface JourAgenda {
  date: string;
  description: string;
  lieu: string;
}

interface EquipeValeurs {
  nom: string;
  typeMission: 'INTER' | 'INTRA' | 'EXT';
  provinceDepart: string;
  debut: string;
  fin: string;
  avion: boolean;
  province: string;
  localite: string;
  membres: string[];
  chef: string;
  agenda: JourAgenda[];
}

interface Valeurs {
  titre: string;
  ligneBudgetaireId: string;
  type: 'MISSION' | 'FORMATION' | 'AUTRE';
  zone: 'INT' | 'EXT';
  urgence: boolean;
  justificationUrgence: string;
  memorial: boolean;
  semaineDecaissement: string;
  contexte: string;
  objectifGlobal: string;
  objectifsSpecifiques: string;
  resultats: string;
  methodologie: string;
  livrables: string;
  cible: string;
  hebergementParAchats: boolean;
  equipes: EquipeValeurs[];
  autresFrais: Array<{ description: string; montant: number }>;
}

const lignes = (s: string) => s.split('\n').map((x) => x.trim()).filter(Boolean);
const equipeVide = (): EquipeValeurs => ({ nom: '', typeMission: 'INTER', provinceDepart: 'Kinshasa', debut: '', fin: '', avion: true, province: '', localite: '', membres: [], chef: '', agenda: [] });
const aujourdHui = () => new Date().toISOString().slice(0, 10);
const plusJours = (iso: string, n: number) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

/** Jours entre deux dates incluses (AAAA-MM-JJ). */
function joursEntre(debut: string, fin: string): string[] {
  if (!debut || !fin || fin < debut) return [];
  const out: string[] = [];
  for (let d = debut; d <= fin && out.length < 120; d = plusJours(d, 1)) out.push(d);
  return out;
}

const depuisTdr = (t: Tdr): Valeurs => ({
  titre: t.titre,
  ligneBudgetaireId: t.ligneBudgetaireId,
  type: t.type,
  zone: t.zone,
  urgence: t.urgence,
  justificationUrgence: t.justificationUrgence ?? '',
  memorial: t.memorial,
  semaineDecaissement: t.semaineDecaissement ?? '',
  contexte: t.contenu.contexte,
  objectifGlobal: t.contenu.objectifGlobal,
  objectifsSpecifiques: t.contenu.objectifsSpecifiques.join('\n'),
  resultats: t.contenu.resultats.join('\n'),
  methodologie: t.contenu.methodologie,
  livrables: t.contenu.livrables.join('\n'),
  cible: t.contenu.cible,
  hebergementParAchats: Boolean(t.contenu.hebergementParAchats),
  equipes: t.equipes.map((e) => ({
    nom: e.nom,
    typeMission: e.typeMission,
    provinceDepart: e.provinceDepart,
    debut: e.debut ?? '',
    fin: e.fin ?? '',
    avion: e.transports.includes('Avion'),
    province: e.destinations[0]?.province ?? '',
    localite: e.destinations[0]?.localite ?? '',
    membres: e.membres.map((m) => m.agentId),
    chef: e.membres.find((m) => m.chef)?.agentId ?? '',
    agenda: (e.agenda ?? []).map((j) => ({ date: j.date, description: j.description, lieu: j.lieu ?? '' })),
  })),
  autresFrais: (t.autresFrais ?? []).map((a) => ({ description: a.description, montant: a.montant })),
});

const versSaisie = (v: Valeurs): TdrSaisie => ({
  titre: v.titre.trim(),
  ligneBudgetaireId: v.ligneBudgetaireId,
  type: v.type,
  zone: v.zone,
  urgence: v.urgence,
  justificationUrgence: v.urgence ? v.justificationUrgence : undefined,
  memorial: v.memorial,
  semaineDecaissement: v.semaineDecaissement || undefined,
  contenu: { contexte: v.contexte, objectifGlobal: v.objectifGlobal, objectifsSpecifiques: lignes(v.objectifsSpecifiques), resultats: lignes(v.resultats), methodologie: v.methodologie, livrables: lignes(v.livrables), cible: v.cible, hebergementParAchats: v.hebergementParAchats },
  equipes:
    v.type === 'MISSION'
      ? v.equipes.map((e) => ({
          nom: e.nom || undefined,
          typeMission: v.zone === 'EXT' ? 'EXT' : e.typeMission,
          provinceDepart: e.provinceDepart,
          debut: e.debut,
          fin: e.fin,
          transports: e.avion ? ['Avion'] : [],
          destinations: [{ province: e.province, localite: e.localite }],
          membres: e.membres.map((agentId) => ({ agentId, chef: agentId === e.chef })),
          agenda: e.agenda.filter((j) => j.description.trim()).map((j) => ({ date: j.date, description: j.description.trim(), lieu: j.lieu.trim() || undefined })),
        }))
      : [],
  autresFrais: v.autresFrais.filter((a) => a.description.trim()).map((a) => ({ description: a.description.trim(), montant: Number(a.montant) || 0 })),
});

interface Etape {
  n: number;
  cle: 'cadre' | 'contenu' | 'equipes' | 'agenda' | 'budget' | 'envoi';
  titre: string;
  sous: string;
}
const ETAPES: Etape[] = [
  { n: 1, cle: 'cadre', titre: 'Cadre', sous: 'type, budget' },
  { n: 2, cle: 'contenu', titre: 'Contenu', sous: 'objectifs, livrables' },
  { n: 3, cle: 'equipes', titre: 'Équipes', sous: 'membres, dates, lieux' },
  { n: 4, cle: 'agenda', titre: 'Agenda', sous: 'jour par jour' },
  { n: 5, cle: 'budget', titre: 'Budget', sous: 'perdiems, achats' },
  { n: 6, cle: 'envoi', titre: 'Pièces et envoi', sous: 'aperçu, soumission' },
];

/** Rédaction d'un TDR en six étapes (maquette émetteur, § 5) : cadre, contenu, équipes, agenda, budget, pièces et envoi. */
export function TdrFormulaire() {
  const { id } = useParams();
  const navigate = useNavigate();
  const session = useSession();
  const u = session!.utilisateur;
  const existant = useTdr(id);
  const agents = useAgents();
  const structures = useStructures();
  const declareMemorial = useDeclareMemorial(u.structureCode);
  // Par défaut, les lignes de la structure de l'utilisateur ; toutes les lignes du Fonds sur demande.
  const [toutesLignes, setToutesLignes] = useState(false);
  const budget = useBudgetLignes(toutesLignes ? undefined : u.structureCode);
  const bareme = useQuery({ queryKey: ['bareme'], queryFn: () => api<{ villes: Array<{ province: string; ville: string; hebergement: number }> }>('/referentiels/baremes/en-vigueur'), staleTime: Infinity });
  const calcul = useCalculMission();
  const actions = useActionsTdr();
  const [resultat, setResultat] = useState<(ResultatCalcul & { agents: Agent[] }) | null>(null);
  const [filtreAgent, setFiltreAgent] = useState('');
  const [etape, setEtape] = useState(1);

  const form = useForm<Valeurs>({
    defaultValues: { titre: '', ligneBudgetaireId: '', type: 'MISSION', zone: 'INT', urgence: false, justificationUrgence: '', memorial: false, semaineDecaissement: '', contexte: '', objectifGlobal: '', objectifsSpecifiques: '', resultats: '', methodologie: '', livrables: '', cible: '', hebergementParAchats: false, equipes: [equipeVide()], autresFrais: [] },
  });
  const equipes = useFieldArray({ control: form.control, name: 'equipes' });
  const frais = useFieldArray({ control: form.control, name: 'autresFrais' });
  const valeurs = useWatch({ control: form.control });

  useEffect(() => {
    if (existant.data) form.reset(depuisTdr(existant.data.tdr));
  }, [existant.data, form]);

  // Calcul automatique du bloc A dès que les équipes sont complètes (sans bouton « Calculer »).
  const cleCalcul = JSON.stringify({ z: valeurs.zone, h: valeurs.hebergementParAchats, t: valeurs.type, e: (valeurs.equipes ?? []).map((e) => ({ ...e, agenda: undefined })) });
  useEffect(() => {
    const v = form.getValues();
    if (v.type !== 'MISSION') return setResultat(null);
    const pretes = v.equipes.filter((e) => e.debut && e.fin && e.membres.length && e.province);
    if (!pretes.length) return setResultat(null);
    const t = setTimeout(() => {
      calcul
        .mutateAsync({ zone: v.zone, hebergementParAchats: v.hebergementParAchats, equipes: pretes.map((e) => ({ nom: e.nom || undefined, typeMission: v.zone === 'EXT' ? 'EXT' : e.typeMission, provinceDepart: e.provinceDepart, debut: e.debut, fin: e.fin, transports: e.avion ? ['Avion'] : [], destinations: [{ province: e.province, localite: e.localite }], membres: e.membres })) })
        .then(setResultat)
        .catch((e: Error) => toast.error(e.message));
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleCalcul]);

  // Agenda : les jours suivent les dates de chaque équipe ; les descriptions déjà saisies sont conservées.
  const cleDates = JSON.stringify((valeurs.equipes ?? []).map((e) => [e?.debut, e?.fin]));
  useEffect(() => {
    const v = form.getValues();
    v.equipes.forEach((e, i) => {
      const jours = joursEntre(e.debut, e.fin);
      const actuel = e.agenda ?? [];
      if (jours.length === actuel.length && jours.every((j, k) => actuel[k]?.date === j)) return;
      form.setValue(
        `equipes.${i}.agenda`,
        jours.map((date) => actuel.find((j) => j.date === date) ?? { date, description: '', lieu: e.localite || '' }),
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleDates]);

  const agentsFiltres = useMemo(() => {
    const f = filtreAgent.trim().toLowerCase();
    return (agents.data ?? []).filter((a) => !f || `${a.prenom} ${a.nom} ${a.fonction} ${a.structureCode}`.toLowerCase().includes(f));
  }, [agents.data, filtreAgent]);
  const nomAgent = (idAgent: string) => {
    const a = agents.data?.find((x) => x.id === idAgent);
    return a ? `${a.prenom} ${a.nom}` : idAgent;
  };

  const mission = valeurs.type === 'MISSION';
  const zone = valeurs.zone ?? 'INT';
  const etapesVisibles = ETAPES.filter((e) => mission || (e.cle !== 'equipes' && e.cle !== 'agenda'));
  const totalA = resultat?.total ?? 0;
  const totalB = (valeurs.autresFrais ?? []).reduce((s, a) => s + (Number(a?.montant) || 0), 0);
  const total = totalA + totalB;
  const ligne = (budget.data ?? []).find((l) => l.id === valeurs.ligneBudgetaireId);
  const structure = structures.data?.find((s) => s.code === u.structureCode);
  const dateMinDepart = plusJours(aujourdHui(), DELAI_DEPART_JOURS);

  // Complétude (maquette : « soumission impossible s'il reste un manque »).
  const points = useMemo<PointCompletude[]>(() => {
    const v = valeurs;
    const p: PointCompletude[] = [];
    p.push({ ok: Boolean(v.ligneBudgetaireId), message: 'Ligne budgétaire choisie', etape: 1 });
    if (v.urgence) p.push({ ok: Boolean(v.justificationUrgence?.trim()), message: "Justification de l'urgence", etape: 1 });
    p.push({ ok: (v.titre?.trim().length ?? 0) >= MIN_TITRE, message: `Titre d'au moins ${MIN_TITRE} caractères`, etape: 2 });
    p.push({ ok: (v.contexte?.trim().length ?? 0) >= MIN_CONTEXTE, message: `Contexte et justification d'au moins ${MIN_CONTEXTE} caractères`, etape: 2 });
    p.push({ ok: (v.objectifGlobal?.trim().length ?? 0) >= MIN_OBJECTIF, message: `Objectif global d'au moins ${MIN_OBJECTIF} caractères`, etape: 2 });
    p.push({ ok: lignes(v.objectifsSpecifiques ?? '').length > 0, message: 'Au moins un objectif spécifique', etape: 2 });
    p.push({ ok: lignes(v.resultats ?? '').length > 0, message: 'Au moins un résultat attendu', etape: 2 });
    p.push({ ok: lignes(v.livrables ?? '').length > 0, message: 'Au moins un livrable', etape: 2 });
    if (mission) {
      const eqs = v.equipes ?? [];
      p.push({ ok: eqs.length > 0, message: 'Au moins une équipe', etape: 3 });
      eqs.forEach((e, i) => {
        const n = e?.nom?.trim() || `Équipe ${i + 1}`;
        p.push({ ok: Boolean(e?.debut && e?.fin && e.fin >= e.debut), message: `${n} : dates de mission cohérentes`, etape: 3 });
        p.push({ ok: Boolean(e?.province), message: `${n} : destination`, etape: 3 });
        p.push({ ok: (e?.membres?.length ?? 0) > 0, message: `${n} : au moins un agent`, etape: 3 });
        if ((e?.membres?.length ?? 0) > 1) p.push({ ok: Boolean(e?.chef && e.membres?.includes(e.chef)), message: `${n} : chef d'équipe désigné`, etape: 3 });
        if (e?.debut && !v.urgence) p.push({ ok: e.debut >= dateMinDepart, message: `${n} : départ au moins ${DELAI_DEPART_JOURS} jours après la soumission (sauf urgence)`, etape: 3 });
        const jours = joursEntre(e?.debut ?? '', e?.fin ?? '');
        if (jours.length) p.push({ ok: jours.every((j) => (e?.agenda ?? []).find((x) => x.date === j)?.description?.trim()), message: `${n} : agenda décrit pour chaque jour`, etape: 4 });
      });
      // Une personne ne peut pas être dans deux équipes aux dates qui se chevauchent.
      const occupations = new Map<string, Array<[string, string, number]>>();
      let chevauchement = '';
      eqs.forEach((e, i) => {
        for (const m of e?.membres ?? []) {
          const liste = occupations.get(m) ?? [];
          if (e?.debut && e?.fin && liste.some(([d, f, k]) => k !== i && e.debut! <= f && e.fin! >= d)) chevauchement = nomAgent(m);
          liste.push([e?.debut ?? '', e?.fin ?? '', i]);
          occupations.set(m, liste);
        }
      });
      p.push({ ok: !chevauchement, message: chevauchement ? `${chevauchement} figure dans deux équipes aux dates qui se chevauchent` : 'Aucun agent dans deux équipes en même temps', etape: 3 });
    }
    p.push({ ok: total > 0, message: 'Montant total supérieur à zéro', etape: 5 });
    if (ligne) p.push({ ok: total <= ligne.disponiblePrevisionnel + 0.005, message: `Total dans le disponible de la ligne (${montant(ligne.disponiblePrevisionnel)}) ; au-delà, une justification sera demandée`, etape: 5, avertissement: true });
    p.push({ ok: Boolean(v.semaineDecaissement && v.semaineDecaissement >= aujourdHui()), message: 'Semaine de décaissement valide', etape: 6 });
    return p;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valeurs, mission, total, ligne, agents.data]);
  const bloquants = points.filter((p) => !p.ok && !p.avertissement);
  const etapeComplete = (n: number) => {
    const ps = points.filter((p) => p.etape === n && !p.avertissement);
    return ps.length > 0 && ps.every((p) => p.ok);
  };

  const enregistrer = async (puisSoumettre: boolean) => {
    const valide = await form.trigger();
    if (!valide) {
      toast.error('Renseignez le titre, la ligne budgétaire, l’objectif global et les dates des équipes.');
      return;
    }
    const v = form.getValues();
    try {
      const corps = versSaisie(v);
      const r = id ? await actions.modifier.mutateAsync({ id, corps }) : await actions.creer.mutateAsync(corps);
      if (!puisSoumettre) {
        toast.success(id ? 'TDR enregistré.' : 'TDR enregistré en brouillon.');
        navigate(`/tdr/${r.tdr.id}`);
        return;
      }
      try {
        await actions.soumettre.mutateAsync(r.tdr.id);
        toast.success('TDR soumis au circuit de validation.');
      } catch (e) {
        const controles = e instanceof ApiError ? (e.corps as { controles?: Array<{ message: string }> } | undefined)?.controles : undefined;
        toast.error(controles?.length ? `Soumission refusée : ${controles.map((c) => c.message).join(' ; ')}` : e instanceof Error ? e.message : 'Soumission impossible.');
      }
      navigate(`/tdr/${r.tdr.id}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Enregistrement impossible.');
    }
  };

  if (id && existant.isLoading) return <Chargement />;
  const indexEtape = Math.max(0, etapesVisibles.findIndex((e) => e.n === etape));
  const courante = etapesVisibles[indexEtape] ?? etapesVisibles[0]!;
  const allerA = (n: number) => setEtape(n);
  const precedente = etapesVisibles[indexEtape - 1];
  const suivante = etapesVisibles[indexEtape + 1];
  const enCours = actions.creer.isPending || actions.modifier.isPending || actions.soumettre.isPending;
  const champObligatoire = <span className="text-red-600"> *</span>;

  return (
    <form onSubmit={(e) => (e.preventDefault(), enregistrer(false))} className="space-y-5">
      <PageHeader
        retour={
          <Link to={id ? `/tdr/${id}` : '/tdr'} className="mb-1 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="size-4" /> Retour
          </Link>
        }
        titre={id ? `Corriger le TDR ${existant.data?.tdr.numero ?? ''}` : 'Nouveau TDR'}
        sousTitre={`Émis pour ${u.structureCode} par ${u.prenom} ${u.nom} · assistant en ${etapesVisibles.length} étapes · le bloc A se calcule automatiquement selon le barème en vigueur.`}
        actions={
          <Button type="submit" variant="outline" disabled={enCours}>
            {id ? 'Enregistrer les corrections' : 'Enregistrer le brouillon'}
          </Button>
        }
      />

      {/* Barre des étapes */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        {etapesVisibles.map((e) => {
          const complete = etapeComplete(e.n);
          const active = e.n === courante.n;
          return (
            <button key={e.n} type="button" onClick={() => allerA(e.n)} className={cn('flex items-center gap-2.5 rounded-lg border bg-card px-3 py-2 text-left transition-colors hover:bg-accent', active && 'border-primary ring-1 ring-primary')} aria-current={active ? 'step' : undefined} aria-label={`Étape ${e.n} · ${e.titre}`}>
              <span className={cn('flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold', complete ? 'bg-emerald-600 text-white' : active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground')}>{complete ? <Check className="size-4" /> : e.n}</span>
              <span className="min-w-0">
                <span className="block text-sm font-medium leading-tight">{e.titre}</span>
                <span className="block truncate text-xs text-muted-foreground">{e.sous}</span>
              </span>
            </button>
          );
        })}
      </div>

      <div className="grid gap-5 lg:grid-cols-[1fr_18rem]">
        <div className="space-y-5">
          {courante.cle === 'cadre' && (
            <Card>
              <CardHeader>
                <CardTitle>1. Cadre du TDR</CardTitle>
                <CardDescription>Le type détermine les sections à remplir ; la zone détermine le barème des perdiems.</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 md:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="type">Type de TDR{champObligatoire}</Label>
                  <SelectNatif id="type" {...form.register('type')}>
                    <option value="MISSION">Mission · déplacement d'équipes</option>
                    <option value="FORMATION">Formation · atelier, séminaire</option>
                    <option value="AUTRE">Autre · travaux intensifs, astreinte</option>
                  </SelectNatif>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="zone">Zone{champObligatoire}</Label>
                  <SelectNatif id="zone" {...form.register('zone')}>
                    <option value="INT">Intérieur (RDC)</option>
                    <option value="EXT">Étranger</option>
                  </SelectNatif>
                </div>
                <div className="space-y-1.5">
                  <Label>Urgence{champObligatoire}</Label>
                  <div className="flex gap-2">
                    <Button type="button" size="sm" variant={!valeurs.urgence ? 'default' : 'outline'} onClick={() => form.setValue('urgence', false)}>
                      Normale
                    </Button>
                    <Button type="button" size="sm" variant={valeurs.urgence ? 'default' : 'outline'} onClick={() => form.setValue('urgence', true)}>
                      Urgente (justification demandée)
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">Une seule notion d'urgence : déclarée par la direction, approuvée par le Directeur Général depuis son parapheur.</p>
                </div>
                <div className="flex flex-col gap-2 pt-1">
                  {declareMemorial && (
                    <label className="flex items-center gap-2 text-sm">
                      <Controller control={form.control} name="memorial" render={({ field }) => <Checkbox checked={field.value} onCheckedChange={(c) => field.onChange(c === true)} />} />
                      Activité du Mémorial (visa du Coordonnateur avant le Directeur)
                    </label>
                  )}
                  <label className="flex items-center gap-2 text-sm">
                    <Controller control={form.control} name="hebergementParAchats" render={({ field }) => <Checkbox checked={field.value} onCheckedChange={(c) => field.onChange(c === true)} />} />
                    Hébergement pris en charge par la Direction des achats
                  </label>
                </div>
                {valeurs.urgence && (
                  <div className="space-y-1.5 md:col-span-2">
                    <Label htmlFor="justif">Justification de l'urgence{champObligatoire}</Label>
                    <Textarea id="justif" rows={2} {...form.register('justificationUrgence')} />
                  </div>
                )}
                <div className="space-y-1.5 md:col-span-2">
                  <ActiviteSousActivite lignes={budget.data ?? []} ligneId={valeurs.ligneBudgetaireId ?? ''} setLigneId={(id) => form.setValue('ligneBudgetaireId', id, { shouldDirty: true, shouldValidate: true })} obligatoire={champObligatoire} />
                  {voitToutLeFonds(u.habilitations) && (
                    <label className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Checkbox checked={toutesLignes} onCheckedChange={(c) => setToutesLignes(c === true)} />
                      Afficher toutes les lignes du Fonds
                    </label>
                  )}
                  <SituationLigneChoisie ligne={ligne} montantDocument={total} montantInitial={existant.data && existant.data.tdr.statut !== 'BROUILLON' ? existant.data.tdr.montantA + existant.data.tdr.montantB : 0} />
                </div>
              </CardContent>
            </Card>
          )}

          {courante.cle === 'contenu' && (
            <Card>
              <CardHeader>
                <CardTitle>2. Contenu</CardTitle>
                <CardDescription>
                  Titre d'au moins {MIN_TITRE} caractères, contexte d'au moins {MIN_CONTEXTE}, objectif global d'au moins {MIN_OBJECTIF} ; une ligne par objectif spécifique, résultat ou livrable.
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 md:grid-cols-2">
                <div className="space-y-1.5 md:col-span-2">
                  <Label htmlFor="titre">Titre{champObligatoire}</Label>
                  <Input id="titre" {...form.register('titre', { required: true, minLength: 5 })} placeholder="Mission d'identification des victimes à …" />
                  <p className="text-xs text-muted-foreground">{valeurs.titre?.trim().length ?? 0} caractères</p>
                </div>
                <div className="space-y-1.5 md:col-span-2">
                  <Label htmlFor="contexte">Contexte et justification{champObligatoire}</Label>
                  <Textarea id="contexte" rows={4} {...form.register('contexte')} />
                  <p className="text-xs text-muted-foreground">{valeurs.contexte?.trim().length ?? 0} caractères</p>
                </div>
                <div className="space-y-1.5 md:col-span-2">
                  <Label htmlFor="objectif">Objectif global{champObligatoire}</Label>
                  <Textarea id="objectif" rows={2} {...form.register('objectifGlobal', { required: true })} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="objectifs">Objectifs spécifiques{champObligatoire}</Label>
                  <Textarea id="objectifs" rows={4} {...form.register('objectifsSpecifiques')} placeholder="Un objectif par ligne" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="resultats">Résultats attendus{champObligatoire}</Label>
                  <Textarea id="resultats" rows={4} {...form.register('resultats')} placeholder="Un résultat par ligne" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="methodologie">Méthodologie</Label>
                  <Textarea id="methodologie" rows={3} {...form.register('methodologie')} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="livrables">Livrables{champObligatoire}</Label>
                  <Textarea id="livrables" rows={3} {...form.register('livrables')} placeholder="Un livrable par ligne" />
                </div>
                <div className="space-y-1.5 md:col-span-2">
                  <Label htmlFor="cible">Cible</Label>
                  <Input id="cible" {...form.register('cible')} />
                </div>
              </CardContent>
            </Card>
          )}

          {courante.cle === 'equipes' && (
            <Card>
              <CardHeader>
                <CardTitle>3. Équipes</CardTitle>
                <CardDescription>Une équipe par ordre de mission : type de mission, province de départ, dates, destination, membres et chef d'équipe. Départ au moins {DELAI_DEPART_JOURS} jours après la soumission, sauf urgence.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {equipes.fields.map((champ, i) => {
                  const e = valeurs.equipes?.[i];
                  const provinceDepart = (e?.provinceDepart ?? '').toLowerCase();
                  const intraProvince = zone === 'INT' && e?.typeMission === 'INTRA' && provinceDepart && provinceDepart !== 'kinshasa';
                  const villes = intraProvince ? (bareme.data?.villes ?? []).filter((v) => v.province.toLowerCase() === provinceDepart) : [];
                  const membres = e?.membres ?? [];
                  return (
                    <div key={champ.id} className="rounded-lg border p-4">
                      <div className="mb-3 flex items-center justify-between">
                        <div className="font-medium">Équipe {i + 1}</div>
                        {equipes.fields.length > 1 && (
                          <Button type="button" variant="ghost" size="sm" onClick={() => equipes.remove(i)}>
                            <Trash2 className="size-4" /> Retirer
                          </Button>
                        )}
                      </div>
                      <div className="grid gap-3 md:grid-cols-3">
                        <div className="space-y-1.5">
                          <Label htmlFor={`eq-${i}-nom`}>Nom</Label>
                          <Input id={`eq-${i}-nom`} {...form.register(`equipes.${i}.nom`)} placeholder={`Équipe ${i + 1}`} />
                        </div>
                        {zone === 'INT' && (
                          <div className="space-y-1.5">
                            <Label htmlFor={`eq-${i}-type`}>Type de mission{champObligatoire}</Label>
                            <SelectNatif id={`eq-${i}-type`} {...form.register(`equipes.${i}.typeMission`)}>
                              <option value="INTER">Interprovinciale</option>
                              <option value="INTRA">Intraprovinciale</option>
                            </SelectNatif>
                          </div>
                        )}
                        {zone === 'INT' && (
                          <div className="space-y-1.5">
                            <Label htmlFor={`eq-${i}-depart`}>Province de départ{champObligatoire}</Label>
                            <SelectNatif id={`eq-${i}-depart`} {...form.register(`equipes.${i}.provinceDepart`)}>
                              {PROVINCES.map((p) => (
                                <option key={p} value={p}>
                                  {p}
                                </option>
                              ))}
                            </SelectNatif>
                          </div>
                        )}
                        <div className="space-y-1.5">
                          <Label htmlFor={`eq-${i}-debut`}>Début{champObligatoire}</Label>
                          <Input id={`eq-${i}-debut`} type="date" {...form.register(`equipes.${i}.debut`, { required: true })} />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor={`eq-${i}-fin`}>Fin{champObligatoire}</Label>
                          <Input id={`eq-${i}-fin`} type="date" {...form.register(`equipes.${i}.fin`, { required: true })} />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor={`eq-${i}-province`}>
                            {zone === 'EXT' ? 'Pays' : 'Province de destination'}
                            {champObligatoire}
                          </Label>
                          <SelectNatif id={`eq-${i}-province`} {...form.register(`equipes.${i}.province`, { required: true })}>
                            <option value="">Choisir</option>
                            {(zone === 'EXT' ? PAYS : PROVINCES).map((p) => (
                              <option key={p} value={p}>
                                {p}
                              </option>
                            ))}
                          </SelectNatif>
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor={`eq-${i}-localite`}>{zone === 'EXT' ? 'Ville' : 'Localité'}</Label>
                          {intraProvince ? (
                            <SelectNatif id={`eq-${i}-localite`} {...form.register(`equipes.${i}.localite`)}>
                              <option value="">Localité du barème</option>
                              {villes.map((v) => (
                                <option key={v.ville} value={v.ville}>
                                  {v.ville} · hébergement {v.hebergement} USD
                                </option>
                              ))}
                            </SelectNatif>
                          ) : (
                            <Input id={`eq-${i}-localite`} {...form.register(`equipes.${i}.localite`)} placeholder={zone === 'EXT' ? 'Genève' : 'Bunia'} />
                          )}
                        </div>
                        <label className="flex items-center gap-2 self-end pb-2 text-sm">
                          <Controller control={form.control} name={`equipes.${i}.avion`} render={({ field }) => <Checkbox checked={field.value} onCheckedChange={(c) => field.onChange(c === true)} />} />
                          Trajet en avion (Go-Pass)
                        </label>
                        {e?.debut && e?.fin && e.fin >= e.debut && (
                          <div className="self-end pb-2 text-xs text-muted-foreground md:col-span-2">
                            Durée : {joursEntre(e.debut, e.fin).length} jour(s), du {dateFr(e.debut)} au {dateFr(e.fin)}
                          </div>
                        )}
                      </div>
                      <div className="mt-4">
                        <Label>
                          Agents de l'équipe ({membres.length}){champObligatoire}
                        </Label>
                        <Input className="my-2" placeholder="Rechercher un agent dans l'annuaire" value={filtreAgent} onChange={(x) => setFiltreAgent(x.target.value)} />
                        <Controller
                          control={form.control}
                          name={`equipes.${i}.membres`}
                          render={({ field }) => (
                            <div className="grid max-h-56 gap-1 overflow-y-auto rounded-md border p-2 sm:grid-cols-2">
                              {agentsFiltres.map((a) => {
                                const choisis: string[] = field.value ?? [];
                                const coche = choisis.includes(a.id);
                                return (
                                  <label key={a.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-muted">
                                    <Checkbox checked={coche} onCheckedChange={(c) => field.onChange(c === true ? [...choisis, a.id] : choisis.filter((x) => x !== a.id))} />
                                    <span className="min-w-0 flex-1 truncate">
                                      {a.prenom} {a.nom}{' '}
                                      <span className="text-muted-foreground">
                                        · {a.categorie} · {a.structureCode}
                                        {a.base === 'COORDINATION' ? ` · ${a.province}` : ''}
                                      </span>
                                    </span>
                                  </label>
                                );
                              })}
                            </div>
                          )}
                        />
                        {membres.length > 0 && (
                          <div className="mt-2 space-y-1.5">
                            <Label htmlFor={`eq-${i}-chef`}>Chef d'équipe{membres.length > 1 ? champObligatoire : ''}</Label>
                            <SelectNatif id={`eq-${i}-chef`} {...form.register(`equipes.${i}.chef`)}>
                              <option value="">Choisir parmi les membres</option>
                              {membres.map((m) => (
                                <option key={m} value={m}>
                                  {nomAgent(m)}
                                </option>
                              ))}
                            </SelectNatif>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                <Button type="button" variant="outline" onClick={() => equipes.append(equipeVide())}>
                  <Plus className="size-4" /> Ajouter une équipe
                </Button>
              </CardContent>
            </Card>
          )}

          {courante.cle === 'agenda' && (
            <Card>
              <CardHeader>
                <CardTitle>4. Agenda</CardTitle>
                <CardDescription>Les jours sont générés à partir des dates de chaque équipe : décrivez le programme et le lieu de chaque jour.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {(valeurs.equipes ?? []).map((e, i) => {
                  const jours = e?.agenda ?? [];
                  return (
                    <div key={i}>
                      <div className="mb-2 font-medium">
                        {e?.nom?.trim() || `Équipe ${i + 1}`} <span className="text-xs font-normal text-muted-foreground">{e?.debut && e?.fin ? `du ${dateFr(e.debut)} au ${dateFr(e.fin)} · ${jours.length} jour(s)` : 'dates à renseigner à l’étape 3'}</span>
                      </div>
                      {jours.length ? (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-32">Jour</TableHead>
                              <TableHead className="w-44">Lieu</TableHead>
                              <TableHead>Programme{champObligatoire}</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {jours.map((j, k) => (
                              <TableRow key={j?.date ?? k}>
                                <TableCell className="whitespace-nowrap text-sm">{dateFr(j?.date)}</TableCell>
                                <TableCell>
                                  <Input {...form.register(`equipes.${i}.agenda.${k}.lieu`)} placeholder={e?.localite || 'Lieu'} />
                                </TableCell>
                                <TableCell>
                                  <Input {...form.register(`equipes.${i}.agenda.${k}.description`)} placeholder="Activités de la journée" />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <p className="text-sm text-muted-foreground">Aucun jour : renseignez les dates de l'équipe.</p>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}

          {courante.cle === 'budget' && (
            <div className="grid gap-5 xl:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>
                    5. Budget · A · Frais de mission {resultat && <span className="num float-right">{montant(resultat.total, true)}</span>}
                  </CardTitle>
                  <CardDescription>{mission ? (resultat ? `Calculé par agent selon le type de mission de son équipe · barème ${resultat.baremeVersion} (taux non modifiables)` : 'Calculé dès que les équipes ont des dates, une destination et des agents.') : 'Pas de frais de mission pour ce type de TDR.'}</CardDescription>
                </CardHeader>
                <CardContent>
                  {resultat && (
                    <>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Agent</TableHead>
                            <TableHead>Nature</TableHead>
                            <TableHead className="text-right">Montant</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {resultat.lignes.map((l, i) => (
                            <TableRow key={i}>
                              <TableCell className="whitespace-nowrap">{nomAgent(l.agentId)}</TableCell>
                              <TableCell>
                                <div>{NATURES[l.nature] ?? l.nature}</div>
                                <div className="text-xs text-muted-foreground">{l.description}</div>
                              </TableCell>
                              <TableCell className="num text-right">{montant(l.montant, true)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      <div className="mt-3">
                        <ControlesListe controles={resultat.controles} />
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>
                    B · Services extérieurs <span className="num float-right">{montant(totalB, true)}</span>
                  </CardTitle>
                  <CardDescription>Carburant, location, fournitures, prestations… traités par les Achats ou les Moyens généraux après autorisation.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  {frais.fields.map((f, i) => (
                    <div key={f.id} className="flex gap-2">
                      <Input className="flex-1" placeholder="Nature et quantité" {...form.register(`autresFrais.${i}.description`)} />
                      <Input className="w-36 text-right" type="number" min={0} step="0.01" placeholder="USD" {...form.register(`autresFrais.${i}.montant`, { valueAsNumber: true })} />
                      <Button type="button" variant="ghost" size="icon" onClick={() => frais.remove(i)} aria-label="Retirer">
                        <Trash2 className="size-4" />
                      </Button>
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={() => frais.append({ description: '', montant: 0 })}>
                    <Plus className="size-4" /> Ajouter une ligne
                  </Button>
                  <div className="rounded-md border bg-muted/30 p-3 text-sm">
                    <div className="flex justify-between">
                      <span>Total du TDR</span>
                      <span className="num font-semibold">{montant(total, true)}</span>
                    </div>
                    {ligne && (
                      <div className={cn('flex justify-between text-xs', total > ligne.disponiblePrevisionnel ? 'text-red-700' : 'text-muted-foreground')}>
                        <span>Disponible de la ligne {ligne.code}</span>
                        <span className="num">{montant(ligne.disponiblePrevisionnel)}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {courante.cle === 'envoi' && (
            <Card>
              <CardHeader>
                <CardTitle>6. Pièces et envoi</CardTitle>
                <CardDescription>Pièces jointes facultatives selon le besoin, semaine de décaissement, aperçu du TDR, contrôle de complétude puis soumission.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="semaine">Semaine de décaissement souhaitée{champObligatoire}</Label>
                    <Input id="semaine" type="date" min={aujourdHui()} {...form.register('semaineDecaissement')} />
                    <p className="text-xs text-muted-foreground">Elle fixe la semaine du plan de décaissement de la trésorerie pour les FED issus de ce TDR.</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Aperçu du TDR</Label>
                    <div>{id ? <BoutonPdf chemin={`/pdf/tdr/${id}`} nom={`${existant.data?.tdr.numero ?? 'TDR'}.pdf`} libelle="Aperçu PDF" /> : <p className="text-xs text-muted-foreground">Disponible après le premier enregistrement du brouillon.</p>}</div>
                  </div>
                </div>
                {id ? (
                  <PiecesJointes objetType="TDR" objetId={id} typesSuggeres={['PROFORMA', 'LISTE_PRESENCE', 'AUTRE']} titre="Pièces jointes" description="Facultatives : proforma, liste des participants, note…" />
                ) : (
                  <div className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">Enregistrez le brouillon pour joindre des pièces (proforma, liste des participants…).</div>
                )}
                <div className="rounded-md border p-3">
                  <Completude points={points} onEtape={allerA} />
                </div>
                <div className="rounded-md border bg-muted/30 p-3 text-sm">
                  <div className="mb-1 font-medium">Documents générés après autorisation</div>
                  <DocumentsGeneres mission={mission} equipes={(valeurs.equipes ?? []).length} totalA={totalA} autresFrais={valeurs.autresFrais ?? []} />
                </div>
              </CardContent>
            </Card>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2">
            <Button type="button" variant="outline" disabled={!precedente} onClick={() => precedente && allerA(precedente.n)}>
              <ArrowLeft className="size-4" /> Étape précédente
            </Button>
            <span className="text-xs text-muted-foreground">Les champs marqués * sont obligatoires pour la soumission.</span>
            {suivante ? (
              <Button type="button" onClick={() => allerA(suivante.n)}>
                Étape suivante <ArrowRight className="size-4" />
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button type="button" variant="outline" asChild>
                  <Link to={id ? `/tdr/${id}` : '/tdr'}>Abandonner</Link>
                </Button>
                <Button type="submit" variant="outline" disabled={enCours}>
                  {id ? 'Enregistrer les corrections' : 'Enregistrer le brouillon'}
                </Button>
                {(!id || existant.data?.tdr.statut === 'BROUILLON') && (
                  <Button type="button" disabled={enCours || bloquants.length > 0} title={bloquants.length ? `${bloquants.length} point(s) à compléter` : undefined} onClick={() => enregistrer(true)}>
                    <Send className="size-4" /> Enregistrer et soumettre
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Colonne latérale : complétude, budget, documents générés, circuit */}
        <aside className="space-y-4 lg:sticky lg:top-4 lg:self-start">
          <Lateral>
            <Completude points={points} onEtape={allerA} compact />
          </Lateral>
          <Lateral titre="Budget">
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span>A · Frais de mission</span>
                <span className="num">{montant(totalA)}</span>
              </div>
              <div className="flex justify-between">
                <span>B · Services extérieurs</span>
                <span className="num">{montant(totalB)}</span>
              </div>
              <div className="flex justify-between border-t pt-1 font-semibold">
                <span>Total du TDR</span>
                <span className="num">{montant(total)}</span>
              </div>
              <div className={cn('text-xs', ligne && total > ligne.disponiblePrevisionnel ? 'text-red-700' : 'text-muted-foreground')}>{ligne ? `Disponible de la ligne ${ligne.code} : ${montant(ligne.disponiblePrevisionnel)}` : 'Choisissez une ligne budgétaire (étape 1).'}</div>
            </div>
          </Lateral>
          <Lateral titre="Documents générés après autorisation">
            <DocumentsGeneres mission={mission} equipes={(valeurs.equipes ?? []).length} totalA={totalA} autresFrais={valeurs.autresFrais ?? []} />
          </Lateral>
          <Lateral titre="Circuit">
            <CircuitPrevu type="TDR" montant={total} operationnel={structure?.operationnelle ?? false} structure={u.structureCode} memorial={Boolean(valeurs.memorial)} />
          </Lateral>
        </aside>
      </div>
    </form>
  );
}

/** Ordres de mission, FED « Perdiems », demandes d'achat ou réquisitions produits à l'autorisation (maquette, § 8). */
function DocumentsGeneres({ mission, equipes, totalA, autresFrais }: { mission: boolean; equipes: number; totalA: number; autresFrais: Array<{ description?: string; montant?: number }> }) {
  const b = autresFrais.filter((a) => a.description?.trim());
  const totalB = b.reduce((s, a) => s + (Number(a.montant) || 0), 0);
  return (
    <ul className="space-y-1 text-sm">
      {mission && (
        <li className="flex justify-between">
          <span>Ordres de mission</span>
          <span className="num">{equipes}</span>
        </li>
      )}
      {mission && (
        <li className="flex justify-between">
          <span>FED « Perdiems » (signatures reprises)</span>
          <span className="num">{montant(totalA)}</span>
        </li>
      )}
      <li className="flex justify-between">
        <span>Demande d'achat ou réquisition ({b.length} ligne{b.length > 1 ? 's' : ''})</span>
        <span className="num">{montant(totalB)}</span>
      </li>
    </ul>
  );
}
